/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import { Common, color } from '@stockbit/ui';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import SUCCESS_MESSAGE from 'constants/successMessage';
import Paywall from 'features/paywall/Wrapper/Widget';
import { SideWidgetAnalytics as WIDGET } from 'features/paywall/analytics';
import Header from './Header';
import PriceAlertContent from './PriceAlertContent';
import AlertLogContent from './AlertLogContent';
import ModalCancelPriceAlert from './ModalCancelPriceAlert';
import ModalAddPriceAlert from './ModalAddPriceAlert';
import { headerHeight } from './styled';
import { ButtonTransparent } from '../styled';
import { Box as ResizableBox } from '../Resizeable/styled';

import { addPriceAlertSelector, cancelPriceAlertSelector, editPriceAlertSelector, effects, selectors } from './slice';

const { Flex, Iconmoon } = Common;

interface PriceAlertProps {
  isPro: boolean;
}

function PriceAlert({ isPro }: PriceAlertProps) {
  const [isModalCancelPriceAlert, setModalCancelPriceAlert] = useState(false);
  const [isModalAddPriceAlert, setModalAddPriceAlert] = useState(false);
  const [isClickAlertLog, setClickAlertLog] = useState(false);

  const [formData, setFormData] = useState({
    stock: '',
    value: '',
    operator: '',
    name: '',
  });

  const [alertId, setAlertId] = useState(0);

  const dispatch = useDispatch();
  const addPriceAlert = useSelector(addPriceAlertSelector);
  const editPriceAlert = useSelector(editPriceAlertSelector);
  const cancelPriceAlert = useSelector(cancelPriceAlertSelector);
  const { isLoading: isLoadingAddPriceAlert } = addPriceAlert;
  const { isLoading: isLoadingEditPriceAlert } = editPriceAlert;
  const { isLoading: isLoadingCancelPriceAlert } = cancelPriceAlert;

  const [resizeableHeight, setResizeableHeight] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);

  const handleShowModalCancel = (e, id) => {
    e.preventDefault();

    setAlertId(id);
    setModalCancelPriceAlert(!isModalCancelPriceAlert);
  };

  const handleSubmitCancelPriceAlert = async () => {
    // @ts-ignore
    const del = await dispatch(effects.cancelPriceAlert(alertId));

    // @ts-ignore
    const { data } = del.payload;

    if (data) {
      handleErrorMessageAPI('Item alert canceled', SUCCESS_MESSAGE.ALERT_GREEN);
    }
  };

  const handleOnCancelModalCancel = () => {
    setModalCancelPriceAlert(false);

    // setAlertId default
    setAlertId(0);

    setFormData({
      stock: '',
      value: '',
      operator: '',
      name: '',
    });
  };

  const handleOnCancelModalAdd = () => {
    setModalAddPriceAlert(false);

    // setAlertId default
    setAlertId(0);

    // set isClickAlertLog to false
    setClickAlertLog(false);

    setFormData({
      stock: '',
      value: '',
      operator: '',
      name: '',
    });
  };

  const handleShowModalAdd = (e, record, isAlertLog) => {
    e.preventDefault();

    if (isAlertLog) {
      const { command, name } = record;

      setClickAlertLog(true);

      setFormData({
        stock: command.stock,
        value: command.value,
        operator: command.operator,
        name,
      });
    } else if (record) {
      const { alert_id: alertid, command, name } = record;

      setAlertId(alertid);

      setFormData({
        stock: command.stock,
        value: command.value,
        operator: command.operator,
        name,
      });
    } else {
      setFormData({
        stock: '',
        value: '',
        operator: '',
        name: '',
      });
    }

    setModalAddPriceAlert(!isModalAddPriceAlert);
  };

  const handleSubmitAddPriceAlert = (name, command, isEdit) => {
    const data: Record<string, any> = {
      name,
      command,
    };

    const action = isEdit ? effects.editPriceAlert : effects.addPriceAlert;

    if (isEdit) {
      data.alertId = alertId;
    }

    // @ts-ignore
    dispatch(action(data));
  };

  useEffect(() => {
    const { isCancel } = cancelPriceAlert;

    if (isCancel) {
      // close modal cancel
      setModalCancelPriceAlert(false);

      // dispatch get all price alert
      // @ts-ignore
      // dispatch(effects.getPriceAlert());

      // dispatch get all alert log
      dispatch(effects.getAlertLog());

      // setAlertId default
      setAlertId(0);
    }
  }, [cancelPriceAlert.isCancel]);

  useEffect(() => {
    const { isSubmit } = addPriceAlert;

    if (isSubmit) {
      // close modal add
      setModalAddPriceAlert(false);

      // dispatch get all price alert
      // @ts-ignore
      dispatch(effects.getPriceAlert());

      // dispatch get all alert log
      dispatch(effects.getAlertLog());

      // setAlertId default
      setAlertId(0);

      // clear form
      setFormData({
        stock: '',
        value: '',
        operator: '',
        name: '',
      });
    }
  }, [addPriceAlert.isSubmit]);

  useEffect(() => {
    const { isSubmit } = editPriceAlert;

    if (isSubmit) {
      // close modal edit
      setModalAddPriceAlert(false);

      // dispatch get all price alert
      // @ts-ignore
      dispatch(effects.getPriceAlert());

      // dispatch get all alert log
      dispatch(effects.getAlertLog());

      // setAlertId default
      setAlertId(0);

      // clear form
      setFormData({
        stock: '',
        value: '',
        operator: '',
        name: '',
      });
    }
  }, [editPriceAlert.isSubmit]);

  useEffect(() => {
    const resizeable = document.getElementsByClassName('sw-price-alert');
    if (resizeable.length) {
      setResizeableHeight(resizeable[0].clientHeight);
    }
    setWindowHeight(window.innerHeight);
  }, []);

  return (
    <>
      <ResizableBox
        className="sw-price-alert"
        defaultSize={{
          height: '50%',
          width: '100%',
        }}
        maxHeight={windowHeight - 40}
        minHeight={50}
        onResizeStop={(e, direction, ref, d) => {
          const h = resizeableHeight + d.height;
          setResizeableHeight(h);
        }}
      >
        <Row
          justify="end"
          style={{
            backgroundColor: color.aliceBlue,
            borderBottom: `solid 1px ${color.gainsboro}`,
          }}
        >
          <Col span={10}>
            <Header>Price Alert</Header>
          </Col>
          <Col span={2} offset={12}>
            {isPro && (
              <ButtonTransparent
                height="40px"
                onClick={handleShowModalAdd}
              >
                <Iconmoon className="icon-compose_plus" style={{ color: '#333' }} />
              </ButtonTransparent>
            )}
          </Col>
        </Row>

        {isPro ? (
          <PriceAlertContent
            handleAddPriceAlert={handleShowModalAdd}
            handleShowModalCancel={handleShowModalCancel}
            maxHeight={resizeableHeight - 50}
          />
        ) : (
          <Paywall height="45vh" source={WIDGET.PRICE_ALERT} />
        )}
      </ResizableBox>
      <div
        style={{
          height: `calc(100vh - ${resizeableHeight}px)`,
          position: 'relative',
          zIndex: 99,
        }}
      >
        <Header
          style={{
            borderBottom: `solid 1px ${color.gainsboro}`,
          }}
        >
          Alert Log
        </Header>
        {isPro ? (
          <AlertLogContent
            handleAddPriceAlert={handleShowModalAdd}
            maxHeight={`calc(100vh - ${resizeableHeight}px - ${headerHeight}px)`}
          />
        ) : (
          <Paywall height="45vh" source={WIDGET.PRICE_ALERT} />
        )}
      </div>

      {/* modal: cancel price alert */}
      <ModalCancelPriceAlert
        loading={isLoadingCancelPriceAlert}
        visible={isModalCancelPriceAlert}
        onSubmit={handleSubmitCancelPriceAlert}
        onCancel={handleOnCancelModalCancel}
      />

      {/* modal: add price alert */}
      <ModalAddPriceAlert
        loading={
          alertId === 0 ? isLoadingAddPriceAlert : isLoadingEditPriceAlert
        }
        visible={isModalAddPriceAlert}
        onSubmit={handleSubmitAddPriceAlert}
        onCancel={handleOnCancelModalAdd}
        formData={formData}
        isEdit={alertId !== 0}
        modalKey={alertId || 'add'}
        isClickAlertLog={isClickAlertLog}
      />
    </>
  );
}

export default PriceAlert;
